import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

export type GateSubjectKind =
    | 'product'
    | 'project'
    | 'professional'
    | 'article';

export interface GateSubject {
    kind: GateSubjectKind;
    id: number;
    title: string;
    /** Pane-quality rendition (detail banner / square), shown behind a gate. */
    hero?: string;
    /** Tile-quality rendition, shown in headers and rows. */
    tile?: string;
}

/**
 * The item the page is about, so a gate opened from anywhere on it can show
 * that item without every CTA threading imagery down by prop.
 *
 * Page lifetime, deliberately not persisted: session-scoped state would outlive
 * its page and put the previous item behind the next gate. A CTA for something
 * more specific than the page (a chosen variant, a tile in a grid) still passes
 * its own image, which wins.
 */
const gateSubjectAtom = atom<GateSubject | null>(null);

export const useGateSubjectValue = (): GateSubject | null =>
    useAtomValue(gateSubjectAtom);

/**
 * Publish the page's subject. Call once at a detail page's root — not per CTA,
 * which is the arrangement this exists to replace.
 */
export const useGateSubject = (subject: GateSubject | null | undefined) => {
    const setSubject = useSetAtom(gateSubjectAtom);
    // Depend on the fields rather than the object so callers can pass a literal
    // without memoising it.
    const kind = subject?.kind;
    const id = subject?.id;
    const title = subject?.title;
    const hero = subject?.hero;
    const tile = subject?.tile;

    useEffect(() => {
        if (!kind || id === undefined || !title) return;
        setSubject({ kind, id, title, hero, tile });
        return () => setSubject(null);
    }, [setSubject, kind, id, title, hero, tile]);
};
